import { TelemetryComponent, TelemetryMode } from './contract';

export interface RouteInfo {
  component: TelemetryComponent;
  mode: TelemetryMode;
  /** Only these routes may carry a jobId; everywhere else it is always "" (ui-late-jobid-analysis.md, rule 1). */
  carriesJobId: boolean;
  storeNum: string;
  aisleCode: string;
}

const STORE_AISLE_ROUTE = /^\/store\/([^/]+)\/aisle\/([^/]+)\/(.+)$/;
const STORE_ROUTE = /^\/store\/([^/]+)\/(.+)$/;

const AISLE_SECTIONS: Record<string, Pick<RouteInfo, 'component' | 'mode' | 'carriesJobId'>> = {
  'seed': { component: 'SEED', mode: 'VIEW', carriesJobId: false },
  'seed/preview': { component: 'SEED', mode: 'PREVIEW', carriesJobId: true },
  'review/visual': { component: 'REVIEW', mode: 'VIEW', carriesJobId: true },
  'review/assortment': { component: 'REVIEW', mode: 'VIEW', carriesJobId: true },
  'review/compare': { component: 'REVIEW', mode: 'COMPARE', carriesJobId: true },
  'customize': { component: 'EDITOR', mode: 'EDIT', carriesJobId: true },
};

const DASHBOARD_SECTIONS = ['dashboard', 'proto-pog', 'combined-pog'];

const route = (
  component: TelemetryComponent,
  mode: TelemetryMode = 'VIEW',
  extra: Partial<RouteInfo> = {},
): RouteInfo => ({ component, mode, carriesJobId: false, storeNum: '', aisleCode: '', ...extra });

const decode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch (e) {
    return value;
  }
};

export const resolveRoute = (rawPathname: string): RouteInfo => {
  const pathname = (rawPathname || '/').replace(/\/+$/, '') || '/';

  if (pathname === '/') {
    return route('WELCOME');
  }

  if (pathname === '/pegx' || pathname.startsWith('/pegx/')) {
    return route('PEGX');
  }

  if (pathname.startsWith('/stores/AdminPage')) {
    return route('ADMIN');
  }

  if (pathname === '/dashboard') {
    return route('DASHBOARD');
  }

  const aisleMatch = STORE_AISLE_ROUTE.exec(pathname);

  if (aisleMatch) {
    const [, storeNum, aisleCode, section] = aisleMatch;
    const known = AISLE_SECTIONS[section];
    const ids = { storeNum: decode(storeNum), aisleCode: decode(aisleCode) };

    return known
      ? route(known.component, known.mode, { ...ids, carriesJobId: known.carriesJobId })
      : route('OTHER', 'VIEW', ids);
  }

  const storeMatch = STORE_ROUTE.exec(pathname);

  if (storeMatch) {
    const [, storeNum, section] = storeMatch;
    const component = DASHBOARD_SECTIONS.includes(section) ? 'DASHBOARD' : 'OTHER';

    return route(component, 'VIEW', { storeNum: decode(storeNum) });
  }

  return route('OTHER');
};
